import React, { createContext, useContext } from "react";
import { flow } from "lodash";
import { usePreloader } from "../../../HOC/withPreloading/withPreloading";
import {
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../../models/useSettingsList/types";
import {
  useSettingsListEditable,
  withFilters,
  withSelectables
} from "../../models/useSettingsList/useSettingsList";
import { AgentEditStatus, AgentEditStatusForm } from "./types";
import {
  crudExtractor,
  copyExistingAgentEditStatusForm,
  createNewAgentEditStatusForm,
  mapAgentEditStatusFormToCreateForm,
  mapAgentEditStatusFormToUpdateForm,
  mapAgentEditStatusToForm
} from "./utils";
import { AppPresence } from "@closerplatform/spinner-openapi";
import { useAgentServices } from "../../../../models/agentServices";

type HookParams = Params<AgentEditStatusForm, AgentEditStatusForm, AgentEditStatus>;
type HookEditable = ListHookEditable<AgentEditStatusForm, AgentEditStatusForm>;
type HookSelectable = ListHookWithSelectables<
  AgentEditStatusForm,
  AgentEditStatusForm,
  HookEditable
>;
type HookFilterable = ListHookWithFilters<AgentEditStatusForm, AgentEditStatusForm, HookSelectable>;

export type AgentEditStatusState = HookFilterable;

const useListHook = flow<[HookParams], HookEditable, HookSelectable, HookFilterable>(
  useSettingsListEditable,
  withSelectables,
  withFilters
);

export const useAgentEditStatusState = (status: AppPresence): AgentEditStatusState => {
  const { setLoadingState } = usePreloader();
  const { spinnerService } = useAgentServices();

  const crud = crudExtractor(status, spinnerService);

  const apiGetItems = async () => {
    const unavailabilityReasons = await crud.apiGetItems();
    return unavailabilityReasons.map((reason) => ({ ...reason, id: reason.reasonSlug }));
  };

  const apiCreateItem = async (form: AgentEditStatusForm) => {
    const item = await crud.apiCreateItem(mapAgentEditStatusFormToCreateForm(form));
    return { ...item, id: item.reasonSlug };
  };

  const apiUpdateItem = async (form: AgentEditStatusForm) => {
    const item = await crud.apiUpdateItem(form.$.id.$, mapAgentEditStatusFormToUpdateForm(form));
    return { ...item, id: item.reasonSlug };
  };

  const apiDeleteItem = (id: string) => crud.apiDeleteItem(id);

  const resetSelected = () => {
    listHook.deselectAll();
  };

  const listHook = useListHook({
    apiGetItems,
    apiCreateItem,
    apiUpdateItem,
    apiDeleteItem,
    mapItemToForm: mapAgentEditStatusToForm,
    createNewForm: createNewAgentEditStatusForm,
    copyExistingForm: copyExistingAgentEditStatusForm,
    onSubmitCallback: resetSelected,
    onDiscardCallback: resetSelected,
    onInitializeResult: setLoadingState,
    saveErrorId: "settings.error.cantSave"
  });

  return listHook;
};

export type AgentEditStatusContextValue = {
  settings: AgentEditStatusState;
  status: AppPresence;
};

const AgentEditStatusContext = createContext<AgentEditStatusContextValue | undefined>(undefined);

export const AgentEditStatusProvider = ({
  children,
  status
}: React.PropsWithChildren<{ status: AppPresence }>) => {
  const values = {
    settings: useAgentEditStatusState(status),
    status
  };

  return (
    <AgentEditStatusContext.Provider value={values}>{children}</AgentEditStatusContext.Provider>
  );
};

export const useAgentEditStatus = () => {
  const context = useContext(AgentEditStatusContext);

  if (context === undefined) {
    throw new Error("useAgentEditStatus must be used within a AgentEditStatusProvider");
  }

  return context;
};
